/**
 * Device ID management for persistent anonymous user tracking
 *
 * Generates and persists a unique device ID in localStorage for tracking
 * anonymous users across sessions. Unlike session_id, device_id is meant
 * to be permanent and identify the device across browser sessions.
 */

const DEVICE_ID_KEY = 'wallstreetle_device_id';

/**
 * Gets the existing device ID from localStorage, or creates a new one if none exists.
 *
 * The device ID persists permanently across page reloads and browser sessions,
 * allowing long-term tracking of device behavior even for anonymous visitors.
 *
 * @returns {string} A UUID v4 device identifier
 */
export function getOrCreateDeviceId(): string {
  try {
    // Try to get existing device ID
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (!deviceId) {
      // Generate new device ID using crypto.randomUUID
      deviceId = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  } catch (error) {
    // Fallback if localStorage is unavailable (e.g., incognito mode, Safari private browsing)
    // Generate a temporary device ID that won't persist across sessions
    console.warn('localStorage unavailable, using temporary device ID:', error);
    return crypto.randomUUID();
  }
}

/**
 * Clears the device ID from localStorage.
 * Useful for testing or if user wants to reset their device identity.
 */
export function clearDeviceId(): void {
  try {
    localStorage.removeItem(DEVICE_ID_KEY);
  } catch (error) {
    console.warn('Failed to clear device ID:', error);
  }
}

/**
 * Gets the current device ID without creating a new one.
 * Returns null if no device ID exists.
 *
 * @returns {string | null} The current device ID or null
 */
export function getDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_ID_KEY);
  } catch (error) {
    console.warn('Failed to get device ID:', error);
    return null;
  }
}

/**
 * One-time migration: Move device ID from legacy key to new key
 * This preserves user tracking history during the consolidation
 *
 * @returns {boolean} True if migration occurred, false if not needed
 */
export function migrateDeviceIdIfNeeded(): boolean {
  try {
    const legacyKey = 'ws:device_id';
    const newKey = 'wallstreetle_device_id';

    // Check if new key already exists (already migrated or new user)
    const existingNewId = localStorage.getItem(newKey);
    if (existingNewId) {
      // Already migrated, cleanup legacy key if it exists
      const legacyId = localStorage.getItem(legacyKey);
      if (legacyId) {
        localStorage.removeItem(legacyKey);
      }
      return false;
    }

    // Check if legacy key exists
    const legacyId = localStorage.getItem(legacyKey);
    if (legacyId) {
      // Migrate: copy to new key and remove old key
      localStorage.setItem(newKey, legacyId);
      localStorage.removeItem(legacyKey);
      return true;
    }

    // No legacy key found, new user - no migration needed
    return false;
  } catch (error) {
    console.warn('[Migration] Failed to migrate device_id:', error);
    return false;
  }
}
